import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

// Theme type tanımları
export interface Theme {
  mode: 'light' | 'dark';
  colors: {
    primary: string;
    primaryHover: string;
    background: string;
    cardBackground: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    shadow: string;
    error: string;
    success: string;
    warning: string;
  };
}

// Light tema
const lightTheme: Theme = {
  mode: 'light',
  colors: {
    primary: '#ff0202',
    primaryHover: '#e00000',
    background: '#ffffff',
    cardBackground: '#ffffff',
    surface: '#f8f9fa',
    text: '#333333',
    textSecondary: '#666666',
    border: '#e0e0e0',
    shadow: 'rgba(0, 0, 0, 0.1)',
    error: '#c62828',
    success: '#2e7d32',
    warning: '#f57c00'
  }
};

// Dark tema
const darkTheme: Theme = {
  mode: 'dark',
  colors: {
    primary: '#ff3333',
    primaryHover: '#ff5555',
    background: '#121212',
    cardBackground: '#1e1e1e',
    surface: '#2d2d2d',
    text: '#ffffff',
    textSecondary: '#b0b0b0',
    border: '#404040',
    shadow: 'rgba(0, 0, 0, 0.3)',
    error: '#ef5350',
    success: '#66bb6a',
    warning: '#ffa726'
  }
};

// Theme context
interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Hook for using theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme provider component
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    // LocalStorage'dan tema tercihini al
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Sistem tercihini kontrol et
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const theme = isDark ? darkTheme : lightTheme;

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    localStorage.setItem('theme', newIsDark ? 'dark' : 'light');
  };

  // Body class'ını güncelle (CSS transitions için)
  useEffect(() => {
    document.body.className = isDark ? 'dark-theme' : 'light-theme';
    document.body.style.backgroundColor = theme.colors.background;
    document.body.style.color = theme.colors.text;
    document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
  }, [isDark, theme]);

  const contextValue: ThemeContextType = {
    theme,
    isDark,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};